import type { DPUserConfig } from '@rehookify/datepicker';
import type { DateTime } from 'luxon';
import type { HTMLMotionProps } from 'motion/react';
import type { DatePickerContextType } from './DatePickerContext.ts';
import {
  useCalendars,
  useDatePickerOffsetPropGetters,
  useDatePickerState
} from '@rehookify/datepicker';
import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useCallback, useMemo, useState } from 'react';
import { PiCaretLeftBold, PiCaretRightBold } from 'react-icons/pi';
import { useIntl } from 'react-intl';
import { useTailwindBreakpoint } from '@_/hooks';
import { MOTION_DURATION_SECONDS, MOTION_EASING } from '@_/theme/constants.ts';
import { isTruthy } from '@_/utils';
import { Button } from '../Button/index.ts';
import { DatePickerContext } from './DatePickerContext.ts';
import { DatePickerMonth } from './DatePickerMonth.tsx';
import { normalizeDatePickerDate, normalizeDateTime } from './utils.ts';

const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const animationProps: HTMLMotionProps<'div'> = {
  initial: 'enter',
  animate: 'default',
  exit: 'exit',
  variants: {
    enter: (direction: number) => {
      return {
        x: direction === 1 ? '100%' : '-100%'
      };
    },
    default: {
      x: 0,
      transition: {
        duration: MOTION_DURATION_SECONDS.base,
        ease: MOTION_EASING.enter,
        type: 'tween'
      }
    },
    exit: (direction: number) => {
      return {
        x: direction === 1 ? '-100%' : '100%',
        transition: {
          duration: MOTION_DURATION_SECONDS.fast,
          ease: MOTION_EASING.exit,
          type: 'tween'
        }
      };
    }
  }
};

export interface DatePickerProps extends Pick<DatePickerContextType, 'include' | 'renderDay'> {
  minDate?: DateTime;
  maxDate?: DateTime;
  numDisplayMonths?: 1 | 2;
  onChange?: (value: DateTime[] | null) => void;
  ranged: boolean;
  size?: 'md' | 'lg';
  timezone?: string;
  value?: DateTime[] | null;
}

export function DatePicker({
  include,
  minDate,
  maxDate,
  numDisplayMonths = 1,
  onChange,
  ranged,
  renderDay,
  size = 'md',
  timezone = localTimezone,
  value
}: DatePickerProps) {
  const { locale } = useIntl();
  const isDesktop = useTailwindBreakpoint('sm');
  const shouldReduceMotion = useReducedMotion();

  const [direction, setDirection] = useState<-1 | 1>(1);
  const [offsetDate, setOffsetDate] = useState<Date>(() =>
    value?.length ? normalizeDateTime(value[0], localTimezone).toJSDate() : new Date()
  );

  const selectedDates = useMemo(
    () => value?.map(date => normalizeDateTime(date, timezone).toJSDate()) || [],
    [value, timezone]
  );

  const min = useMemo(
    () => (minDate ? normalizeDateTime(minDate, timezone).toJSDate() : undefined),
    [minDate, timezone]
  );

  const max = useMemo(
    () => (maxDate ? normalizeDateTime(maxDate, timezone).toJSDate() : undefined),
    [maxDate, timezone]
  );

  const handleDateChange = useCallback<DPUserConfig['onDatesChange']>(
    jsDates => {
      onChange?.(jsDates.map(date => normalizeDatePickerDate(date, timezone)).filter(isTruthy));
    },
    [onChange, timezone]
  );

  const numMonths = isDesktop ? numDisplayMonths : 1;

  const dpState = useDatePickerState({
    calendar: {
      mode: 'static',
      offsets: [...Array(numMonths - 1).keys()].map((_, i) => i + 1)
    },
    dates: {
      minDate: min,
      maxDate: max,
      mode: ranged ? 'range' : 'single',
      selectSameDate: true
    },
    locale: {
      locale,
      weekday: 'narrow'
    },
    onDatesChange: handleDateChange,
    onOffsetChange: setOffsetDate,
    offsetDate,
    selectedDates
  });

  const { calendars } = useCalendars(dpState);

  const { addOffset, subtractOffset } = useDatePickerOffsetPropGetters(dpState);

  /* oxlint-disable-next-line typescript/unbound-method */
  const { onClick: onClickPrev, ...prevProps } = subtractOffset({
    months: numMonths
  });

  const handleClickPrev = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setDirection(-1);
      onClickPrev?.(event);
    },
    [onClickPrev]
  );

  /* oxlint-disable-next-line typescript/unbound-method */
  const { onClick: onClickNext, ...nextProps } = addOffset({
    months: numMonths
  });

  const handleClickNext = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setDirection(1);
      onClickNext?.(event);
    },
    [onClickNext]
  );

  const context = useMemo(
    () => ({ dpState, include, renderDay, timezone }),
    [dpState, include, renderDay, timezone]
  );

  return (
    <DatePickerContext value={context}>
      <div className="relative pt-px select-none">
        <div className="absolute z-0 grid h-10 w-full grid-cols-7 items-center justify-between sm:flex">
          <div className="justify-self-center">
            <Button
              {...prevProps}
              icon={<PiCaretLeftBold />}
              onClick={handleClickPrev}
              rounded
              size="md"
              variant="subtle"
            />
          </div>
          <div className="col-span-5 sm:hidden" />
          <div className="justify-self-center">
            <Button
              {...nextProps}
              icon={<PiCaretRightBold />}
              onClick={handleClickNext}
              rounded
              size="md"
              variant="subtle"
            />
          </div>
        </div>

        <AnimatePresence custom={direction} initial={false} mode="popLayout">
          <motion.div
            key={`${calendars[0].year}-${calendars[0].month}`}
            className={clsx(
              'grid grid-cols-1 place-items-center items-start gap-x-4',
              numMonths === 1 && 'grid-cols-1',
              numMonths === 2 && 'grid-cols-2'
            )}
            custom={direction}
            {...animationProps}
            exit={shouldReduceMotion ? undefined : animationProps.exit}
            initial={shouldReduceMotion ? false : animationProps.initial}
          >
            {calendars.map(calendar => (
              <div
                key={`${calendar.year}-${calendar.month}`}
                className={clsx(
                  'w-full',
                  size === 'md' && 'min-w-72 sm:w-72',
                  size === 'lg' && 'min-w-80 sm:w-80'
                )}
              >
                <DatePickerMonth calendar={calendar} />
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </DatePickerContext>
  );
}
